/**
 * Client for the FastAPI aero backend (Cloud Run `aero-coach-backend`).
 *
 * Centralises backend URL resolution, which was previously duplicated in
 * SessionDeepAnalysisView.vue and composables/useAeroCoach.ts.
 */
import { authenticatedFetch } from '@/services/backendAuth'

export function getBackendUrl(): string {
  const envUrl = import.meta.env.VITE_AERO_COACH_BACKEND_URL as string
  if (envUrl) return envUrl
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8080'
  }
  // Fallback production URL
  return 'https://aero-coach-backend-hwtr2rmgfa-nn.a.run.app'
}

export interface AnalysisOverrides {
  mass?: number
  crr?: number
  air_bias?: number
  eta?: number
  air_factor?: number
  telemetry_path?: string
}

/**
 * Queue a re-analysis of a session. Returns as soon as the job is accepted —
 * the backend runs the pipeline as a background task.
 *
 * Call with NO overrides once the session document has been saved: the pipeline
 * resolves each parameter as `request override -> Firestore field -> default`,
 * so omitting the body makes the stored document the single source of truth and
 * keeps the persisted params in step with the results computed from them.
 *
 * @throws Error carrying the backend `detail` message on a non-2xx response.
 */
export async function triggerSessionAnalysis(
  sessionId: string,
  overrides: AnalysisOverrides = {}
): Promise<void> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/sessions/${sessionId}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(overrides)
  })

  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to start analysis backend job.')
  }
}

/**
 * Fetch processed insights for a session.
 *
 * Returns `null` when results are not available yet. The endpoint answers 200
 * with `{ status: 'processing' }` while a run is in flight, so the presence of
 * `time_series` — not the status code — is what distinguishes a real payload.
 *
 * @throws Error when the backend reports the analysis failed.
 */
export async function fetchAeroInsights(sessionId: string): Promise<any | null> {
  const res = await authenticatedFetch(`${getBackendUrl()}/api/v1/sessions/${sessionId}/aero-insights`)

  if (res.status === 404) return null

  if (!res.ok) {
    const errData = await res.json().catch(() => ({} as any))
    throw new Error(errData.detail || 'Failed to load aero insights.')
  }

  const data = await res.json().catch(() => null)
  if (!data || !data.time_series) return null
  return data
}
